import { computed, ref, type ComputedRef, type Ref } from 'vue'
import * as THREE from 'three'
import { v4 as uuidv4 } from 'uuid'
import type { SceneManager } from '@/three/SceneManager'
import type { Point3D, SceneObject, EditorTool } from '@/types/scene'
import type { useSceneStore, useSelectionStore } from '@/stores/scene'
import type { HistoryManager } from '@/three/HistoryManager'
import { getClickWorldPosition, type ClickGroundOptions } from '@/three/DragHandler'
import { getPipePresetById } from '@/components-lib/pipePresets'
import { RIVER_DEFAULTS } from '@/components-lib/infrastructure/riverDefaults'
type SceneStore = ReturnType<typeof useSceneStore>
type SelectionStore = ReturnType<typeof useSelectionStore>

export interface LayoutStrokeToolDeps {
  getSm: () => SceneManager | undefined
  sceneStore: SceneStore
  selectionStore: SelectionStore
  histManager: HistoryManager
  interactionReadonly: ComputedRef<boolean>
  currentFloorId: Ref<string>
  getEditorTool: () => EditorTool
  getRoadStrokeColor: () => string
  getRoadStrokeShowCenterLine: () => boolean
  getRoadStrokeHasCurb: () => boolean
  getRoadStrokeCurbColor: () => string
  getPipePresetId: () => string
  getWallStrokeThickness: () => number
  getWallStrokeHeight: () => number
}

function getGroundY(deps: LayoutStrokeToolDeps) {
  const floor = deps.sceneStore.activeFloors().find(f => f.id === deps.currentFloorId.value)
  return floor?.elevation ?? 0
}

function snapOpts(deps: LayoutStrokeToolDeps): ClickGroundOptions {
  const g = deps.sceneStore.activeScene()?.scene?.gridSize ?? 1
  return { gridSize: g, groundY: getGroundY(deps) }
}

function orthogonalFromLast(last: Point3D, raw: Point3D): Point3D {
  const dx = raw.x - last.x
  const dz = raw.z - last.z
  if (Math.abs(dx) >= Math.abs(dz)) {
    return { x: raw.x, y: raw.y, z: last.z }
  }
  return { x: last.x, y: raw.y, z: raw.z }
}

export function createLayoutStrokeTool(deps: LayoutStrokeToolDeps) {
  const draftWaypoints = ref<Point3D[]>([])
  const draftPreviewCursor = ref<Point3D | null>(null)
  const draftCloseHighlight = ref(false)
  const layoutRoot = new THREE.Group()
  layoutRoot.name = '__layoutStroke__'

  let rafPending = false
  function scheduleSync() {
    if (rafPending) return
    rafPending = true
    requestAnimationFrame(() => {
      rafPending = false
      syncDraftVisuals()
    })
  }

  function ensureRoot() {
    const sm = deps.getSm()
    if (!sm) return
    if (!sm.scene.getObjectByName(layoutRoot.name)) {
      sm.scene.add(layoutRoot)
    }
  }

  function clearRoot() {
    while (layoutRoot.children.length) {
      const c = layoutRoot.children.pop()
      if (!c) break
      if (c instanceof THREE.Mesh || c instanceof THREE.Line) {
        c.geometry.dispose()
        const m = c.material
        if (Array.isArray(m)) m.forEach(mm => mm.dispose())
        else m.dispose()
      }
    }
  }

  function drawDraft() {
    const mode = deps.getEditorTool()
    if (mode !== 'roadStroke' && mode !== 'wallStroke' && mode !== 'riverStroke' && mode !== 'pipeStroke') return
    const color = mode === 'roadStroke' ? 0x22c55e : mode === 'wallStroke' ? 0xa855f7 : mode === 'riverStroke' ? 0x1a5fb4 : 0x38bdf8
    const pipePreset = getPipePresetById(deps.getPipePresetId())
    const wallThickness = Math.max(0.1, deps.getWallStrokeThickness())
    const wallHeight = Math.max(0.5, deps.getWallStrokeHeight())
    const roadWidth = 7
    const riverWidth = 5
    const pipeDiameter = Math.max(0.05, pipePreset.diameter)

    const addSegmentPreview = (a: Point3D, b: Point3D, rubber = false) => {
      const dx = b.x - a.x
      const dz = b.z - a.z
      const len = Math.hypot(dx, dz)
      if (len < 1e-4) return
      const midX = (a.x + b.x) / 2
      const midZ = (a.z + b.z) / 2
      const rotY = Math.atan2(dx, dz)
      const mat = new THREE.MeshBasicMaterial({
        color: rubber ? 0xfde047 : color,
        transparent: true,
        opacity: rubber ? 0.35 : 0.5,
      })

      if (mode === 'pipeStroke') {
        const r = Math.max(0.025, pipeDiameter / 2)
        const mesh = new THREE.Mesh(new THREE.CylinderGeometry(r, r, len, 12), mat)
        mesh.position.set(midX, a.y + r + 0.02, midZ)
        const dir = new THREE.Vector3(dx, 0, dz).normalize()
        const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir)
        mesh.setRotationFromQuaternion(q)
        layoutRoot.add(mesh)
        return
      }

      if (mode === 'wallStroke') {
        const mesh = new THREE.Mesh(new THREE.BoxGeometry(wallThickness, wallHeight, len), mat)
        mesh.position.set(midX, a.y + wallHeight / 2 + 0.02, midZ)
        mesh.rotation.y = rotY
        layoutRoot.add(mesh)
        return
      }

      if (mode === 'riverStroke') {
        const riverH = 0.08
        const mesh = new THREE.Mesh(new THREE.BoxGeometry(riverWidth, riverH, len), mat)
        mesh.position.set(midX, a.y + riverH / 2 + 0.02, midZ)
        mesh.rotation.y = rotY
        layoutRoot.add(mesh)
        return
      }

      const roadH = 0.08
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(roadWidth, roadH, len), mat)
      mesh.position.set(midX, a.y + roadH / 2 + 0.02, midZ)
      mesh.rotation.y = rotY
      layoutRoot.add(mesh)
    }

    for (const wp of draftWaypoints.value) {
      const p = new THREE.Mesh(
        new THREE.SphereGeometry(0.22, 10, 8),
        new THREE.MeshBasicMaterial({ color })
      )
      p.position.set(wp.x, wp.y, wp.z)
      layoutRoot.add(p)
    }
    if (draftWaypoints.value.length >= 1) {
      if (draftWaypoints.value.length >= 2) {
        for (let i = 0; i < draftWaypoints.value.length - 1; i++) {
          addSegmentPreview(draftWaypoints.value[i]!, draftWaypoints.value[i + 1]!)
        }
      }
      const last = draftWaypoints.value[draftWaypoints.value.length - 1]!
      const preview = draftPreviewCursor.value
      if (preview) {
        addSegmentPreview(last, preview, true)
      }
    }
    if (draftCloseHighlight.value && draftWaypoints.value.length >= 2) {
      const first = draftWaypoints.value[0]!
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.35, 0.55, 24),
        new THREE.MeshBasicMaterial({ color: 0xfbbf24, side: THREE.DoubleSide })
      )
      ring.rotation.x = -Math.PI / 2
      ring.position.set(first.x, first.y + 0.02, first.z)
      layoutRoot.add(ring)
    }
  }

  function syncDraftVisuals() {
    ensureRoot()
    clearRoot()
    drawDraft()
    deps.getSm()?.markDirty()
  }

  function distXZ(a: Point3D, b: Point3D) {
    return Math.hypot(a.x - b.x, a.z - b.z)
  }

  function appendWaypoint(ev: MouseEvent) {
    const sm = deps.getSm()
    if (!sm || deps.interactionReadonly.value) return false
    const mode = deps.getEditorTool()
    if (mode !== 'roadStroke' && mode !== 'wallStroke' && mode !== 'riverStroke' && mode !== 'pipeStroke') return false
    const canvas = sm.renderer.domElement
    let pos = getClickWorldPosition(ev, canvas, sm.camera, snapOpts(deps))
    if (!pos) return false
    const grid = deps.sceneStore.activeScene()?.scene?.gridSize ?? 1
    const p: Point3D = { x: pos.x, y: pos.y, z: pos.z }

    if (draftWaypoints.value.length >= 2) {
      const first = draftWaypoints.value[0]!
      if (distXZ(p, first) < 0.35 * grid) {
        finishStroke(true)
        return true
      }
    }

    if (ev.shiftKey && draftWaypoints.value.length >= 1) {
      const last = draftWaypoints.value[draftWaypoints.value.length - 1]!
      Object.assign(p, orthogonalFromLast(last, p))
    }

    draftWaypoints.value.push(p)
    draftPreviewCursor.value = null
    syncDraftVisuals()
    return true
  }

  function onDraftPointerMove(ev: MouseEvent) {
    const mode = deps.getEditorTool()
    if (mode !== 'roadStroke' && mode !== 'wallStroke' && mode !== 'riverStroke' && mode !== 'pipeStroke') return
    if (draftWaypoints.value.length < 1) {
      draftPreviewCursor.value = null
      draftCloseHighlight.value = false
      return
    }
    const sm = deps.getSm()
    if (!sm) return
    let pos = getClickWorldPosition(ev, sm.renderer.domElement, sm.camera, snapOpts(deps))
    if (!pos) return
    let p: Point3D = { x: pos.x, y: pos.y, z: pos.z }
    if (ev.shiftKey) {
      const last = draftWaypoints.value[draftWaypoints.value.length - 1]!
      p = orthogonalFromLast(last, p)
    }
    draftPreviewCursor.value = p
    const grid = deps.sceneStore.activeScene()?.scene?.gridSize ?? 1
    const first = draftWaypoints.value[0]!
    draftCloseHighlight.value = draftWaypoints.value.length >= 2 && distXZ(p, first) < 0.35 * grid
    scheduleSync()
  }

  function finishStroke(closed: boolean) {
    if (draftWaypoints.value.length < 2) return
    const mode = deps.getEditorTool()
    const worldWaypoints = draftWaypoints.value.map(w => ({ ...w }))
    const anchor = worldWaypoints[0]!
    const waypoints = worldWaypoints.map((w) => ({
      x: w.x - anchor.x,
      y: w.y - anchor.y,
      z: w.z - anchor.z,
    }))
    const typeByMode: Record<string, string> = {
      roadStroke: 'infra_road_polyline',
      wallStroke: 'infra_wall_polyline',
      riverStroke: 'infra_river',
      pipeStroke: 'pipe_polyline',
    }
    const nameByMode: Record<string, string> = {
      roadStroke: '折线道路',
      wallStroke: '折线围墙',
      riverStroke: '河流',
      pipeStroke: '折线管线',
    }
    const base: Omit<SceneObject, 'id'> = {
      type: typeByMode[mode]!,
      name: nameByMode[mode]!,
      position: [anchor.x, anchor.y, anchor.z],
      rotationY: 0,
      params: {},
      label: { text: nameByMode[mode]!, visible: false },
    }
    if (mode === 'roadStroke') {
      base.params = {
        waypoints,
        waypointSpace: 'local',
        closed,
        lanes: 2,
        width: 7,
        color: deps.getRoadStrokeColor(),
        showCenterLine: deps.getRoadStrokeShowCenterLine(),
        showCurbs: deps.getRoadStrokeHasCurb(),
        curb: deps.getRoadStrokeHasCurb(),
        curbColor: deps.getRoadStrokeCurbColor(),
        lineStyle: 'dashed',
        dashDensity: 'sparse',
      }
    } else if (mode === 'wallStroke') {
      base.params = {
        waypoints,
        waypointSpace: 'local',
        closed,
        thickness: deps.getWallStrokeThickness(),
        height: deps.getWallStrokeHeight(),
        color: '#8a8a8a',
      }
    } else if (mode === 'riverStroke') {
      base.params = {
        waypoints,
        waypointSpace: 'local',
        closed,
        ...RIVER_DEFAULTS,
        edgeSeed: Math.floor(Math.random() * 1000),
        hasRocks: true,
        hasReeds: true,
      }
    } else {
      const preset = getPipePresetById(deps.getPipePresetId())
      base.params = {
        waypoints,
        waypointSpace: 'local',
        closed,
        diameter: preset.diameter,
        color: preset.color,
        flowEnabled: true,
        flowPreview: true,
        flowSpeed: 1.8,
        flowOpacity: 0.72,
        flowColor: '#00e5ff',
        autoFittings: [],
      }
    }
    const obj: SceneObject = {
      ...base,
      id: uuidv4(),
    } as SceneObject

    deps.histManager.push(deps.sceneStore.getEditorSnapshot())
    deps.sceneStore.addObject(obj)
    deps.selectionStore.deselect()
    // 完成一条折线（Enter/双击/点起点闭合）后必须退出绘制工具，不能停留在"待绘制"状态
    // 直接开始下一条——否则连续误触很难控制，要画下一条必须重新点组件按钮进入。
    deps.selectionStore.setEditorTool('none')
    draftWaypoints.value = []
    draftPreviewCursor.value = null
    draftCloseHighlight.value = false
    syncDraftVisuals()
  }

  function clearDraft() {
    draftWaypoints.value = []
    draftPreviewCursor.value = null
    draftCloseHighlight.value = false
    syncDraftVisuals()
  }

  function disposeLayoutStrokeTool() {
    clearRoot()
    layoutRoot.parent?.remove(layoutRoot)
  }

  const hasDraft = computed(() => draftWaypoints.value.length > 0)

  return {
    appendWaypoint,
    onDraftPointerMove,
    finishStroke: () => finishStroke(false),
    finishStrokeClosed: () => finishStroke(true),
    clearDraft,
    syncDraftVisuals,
    disposeLayoutStrokeTool,
    hasDraft,
  }
}
